/**
 * Price Range Filter
 * Gère la sélection de tranches de prix avec checkboxes multiples
 * Auto-coche les tranches intermédiaires pour créer une plage continue
 */

const initPriceRangeFilters = () => {
  const priceRangeFilters = document.querySelectorAll("[data-price-range-filter]");

  if (!priceRangeFilters.length) return;

  priceRangeFilters.forEach((filter) => {
    const rangeCheckboxes = filter.querySelectorAll('input[type="checkbox"][name^="price_range_"]');
    const minInput = filter.querySelector('input[data-price-input="min"]');
    const maxInput = filter.querySelector('input[data-price-input="max"]');
    const form = filter.closest("form");

    if (!rangeCheckboxes.length || !minInput || !maxInput) return;

    // Set pour stocker les indices cochés MANUELLEMENT par l'utilisateur
    const userCheckedIndices = new Set();

    /**
     * Auto-coche les tranches intermédiaires et met à jour les inputs cachés
     */
    const updatePriceRange = () => {
      if (userCheckedIndices.size === 0) {
        // Aucune tranche sélectionnée : tout décocher et réinitialiser
        rangeCheckboxes.forEach((cb) => {
          cb.checked = false;
          cb.classList.remove("auto-checked");
        });
        minInput.value = "";
        maxInput.value = "";
        return;
      }

      // Trouver l'index min et max des tranches cochées par l'utilisateur
      const indices = Array.from(userCheckedIndices);
      const minIndex = Math.min(...indices);
      const maxIndex = Math.max(...indices);

      // Auto-cocher toutes les tranches entre min et max
      rangeCheckboxes.forEach((cb) => {
        const index = parseInt(cb.dataset.rangeIndex);
        const label = cb.closest("label");

        if (index >= minIndex && index <= maxIndex) {
          // Tranche dans la plage : cocher
          cb.checked = true;

          // Si pas cochée manuellement, marquer comme auto-cochée
          if (!userCheckedIndices.has(index)) {
            cb.classList.add("auto-checked");
            cb.disabled = true; // Empêcher de décocher
            if (label) {
              label.classList.add("opacity-50", "cursor-not-allowed");
              label.classList.remove("cursor-pointer", "hover:text-pinkyJeannette");
            }
          } else {
            cb.classList.remove("auto-checked");
            cb.disabled = false;
            if (label) {
              label.classList.remove("opacity-50", "cursor-not-allowed");
              label.classList.add("cursor-pointer", "hover:text-pinkyJeannette");
            }
          }
        } else {
          // Tranche hors plage : décocher et réactiver
          cb.checked = false;
          cb.disabled = false;
          cb.classList.remove("auto-checked");
          if (label) {
            label.classList.remove("opacity-50", "cursor-not-allowed");
            label.classList.add("cursor-pointer", "hover:text-pinkyJeannette");
          }
        }
      });

      // Calculer min et max globaux
      const allChecked = Array.from(rangeCheckboxes).filter((cb) => cb.checked);
      if (allChecked.length > 0) {
        const minPriceData = Math.min(...allChecked.map((cb) => parseFloat(cb.dataset.priceMin)));
        const maxPriceData = Math.max(...allChecked.map((cb) => parseFloat(cb.dataset.priceMax)));

        // Shopify attend des euros dans l'URL (filter.v.price.gte/lte)
        // Donc si data-price-min est en centimes, on divise par 100
        minInput.value = (minPriceData / 100).toString();
        maxInput.value = (maxPriceData / 100).toString();
      }
    };

    /**
     * Initialise l'état au chargement en détectant les filtres actifs
     */
    const initFromActiveFilters = () => {
      // Les valeurs dans les inputs sont déjà dans le format Shopify
      // Après un reload AJAX, Shopify retourne en centimes
      // Au premier chargement, on a écrit en euros (20), mais après reload c'est en centimes (2000)
      // Donc on doit détecter le format
      const minVal = parseFloat(minInput.value) || 0;
      const maxVal = parseFloat(maxInput.value) || 0;

      // Si la valeur est < 1000, c'est probablement en euros (conversion JS)
      // Si > 1000, c'est en centimes (retour Shopify)
      const currentMin = minVal < 1000 ? minVal * 100 : minVal;
      const currentMax = maxVal < 1000 ? maxVal * 100 : maxVal;

      if (currentMin === 0 && currentMax === 0) {
        return;
      }

      // Stratégie: trouver la première et dernière tranche qui touchent la plage
      // L'utilisateur a probablement coché ces deux là (les autres sont auto-cochées)
      let firstIndex = null;
      let lastIndex = null;

      rangeCheckboxes.forEach((cb) => {
        const rangeMin = parseFloat(cb.dataset.priceMin);
        const rangeMax = parseFloat(cb.dataset.priceMax);
        const index = parseInt(cb.dataset.rangeIndex);

        // Si la tranche chevauche VRAIMENT la plage active (comparaisons strictes pour éviter les doublons aux bornes)
        // Exemple: pour 2000-5000, on veut tranche 3 (2000-5000) mais PAS tranche 2 (1000-2000)
        if (rangeMin < currentMax && rangeMax > currentMin) {
          if (firstIndex === null) {
            firstIndex = index;
          }
          lastIndex = index;
        }
      });

      // Ajouter la première et la dernière au Set user
      if (firstIndex !== null) {
        userCheckedIndices.add(firstIndex);
      }
      if (lastIndex !== null && lastIndex !== firstIndex) {
        userCheckedIndices.add(lastIndex);
      }

      // Maintenant mettre à jour l'affichage
      updatePriceRange();
    };

    // Event listener sur les checkboxes
    rangeCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", (_e) => {
        const index = parseInt(checkbox.dataset.rangeIndex);

        // Les checkboxes disabled ne devraient pas trigger de change,
        // mais au cas où...
        if (checkbox.disabled) {
          return;
        }

        if (checkbox.checked) {
          // Cocher : ajouter au Set des indices user
          userCheckedIndices.add(index);
        } else {
          // Décocher : retirer du Set des indices user
          userCheckedIndices.delete(index);
        }

        updatePriceRange();

        // Déclencher l'event AJAX
        if (form) {
          form.dispatchEvent(new CustomEvent("priceRangeChange", { bubbles: true }));
        }
      });
    });

    // Initialisation au chargement
    initFromActiveFilters();
  });
};

// Initialiser au chargement de la page
document.addEventListener("DOMContentLoaded", initPriceRangeFilters);

// Ré-initialiser après un reload AJAX des filtres
document.addEventListener("filtersReloaded", initPriceRangeFilters);
